import React from 'react';
import i18n from 'util/i18n';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Text from 'components/Form/Text';
import ValidationElement from 'components/Form/ValidationElement';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonScrollIntoView, CommonRequired
} from 'types/props';

import { ErrorCode } from 'redux/modules/application/reducers/error';
import { DateModel } from 'forms/shared/types';
import Show from 'components/Form/Show';
import Textarea from 'components/Form/Textarea';
import Dropdown from 'components/Form/Dropdown';
import {
  chargeTypes, criminalSentenceOptions, criminalOutcomeSentenceOverOneYearOptions
} from 'constants/enums/legalOptions';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Checkbox from 'components/Form/Checkbox';
import { LegalCriminalChargeModel } from 'forms/sections/legal/criminalconviction/types';
import DateRange from 'components/Form/DateRange';
import Branch from 'components/Form/Branch';

interface Props extends Partial<LegalCriminalChargeModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  attribute?: string
  offenseDate?: DateModel
}

export default class CriminalCharge extends ValidationElement<Props> {
  update = (queue: Partial<LegalCriminalChargeModel>) => {
    this.props.onUpdate({
      CourtCharge: this.props.CourtCharge,
      ChargeType: this.props.ChargeType,
      ChargeTypeOtherExplanation: this.props.ChargeTypeOtherExplanation,
      CourtOutcome: this.props.CourtOutcome,
      CourtOutcomeOtherExplanation: this.props.CourtOutcomeOtherExplanation,
      ConvictedDate: this.props.ConvictedDate,
      SentenceJudgements: this.props.SentenceJudgements,
      SentenceJudgementOtherExplanation: this.props.SentenceJudgementOtherExplanation,
      ProbationDates: this.props.ProbationDates,
      IncarcerationDates: this.props.IncarcerationDates,
      GrantedParole: this.props.GrantedParole,
      GrantedParoleDates: this.props.GrantedParoleDates,

      ...queue,
    });
  };

  updateCourtCharge = (values: LegalCriminalChargeModel['CourtCharge']) => {
    this.update({
      CourtCharge: values,
    });
  };

  updateChargeType = (values: LegalCriminalChargeModel['ChargeType']) => {
    this.update({
      ChargeType: values,
    });
  };

  updateChargeTypeOtherExplanation = (values: LegalCriminalChargeModel['ChargeTypeOtherExplanation']) => {
    this.update({
      ChargeTypeOtherExplanation: values,
    });
  };

  updateCourtOutcome = (values: LegalCriminalChargeModel['CourtOutcome']) => {
    this.update({
      CourtOutcome: values,
      CourtOutcomeOtherExplanation: values?.value?.includes('Other') ? this.props.CourtOutcomeOtherExplanation : {},
    });
  };

  updateCourtOutcomeOtherExplanation = (values: LegalCriminalChargeModel['CourtOutcomeOtherExplanation']) => {
    this.update({
      CourtOutcomeOtherExplanation: values,
    });
  };

  updateConvictedDate = (values: LegalCriminalChargeModel['ConvictedDate']) => {
    this.update({
      ConvictedDate: values,

    });
  };

  updateSentenceJudgements = (values: LegalCriminalChargeModel['SentenceJudgements']) => {
    const sentenceJudgements = values?.value;
    const selected = [...((this.props.SentenceJudgements || {}).values || [])];
    if (selected.includes(sentenceJudgements)) {
      selected.splice(selected.indexOf(sentenceJudgements), 1);
    } else {
      selected.push(sentenceJudgements);
    }
    // if "Other" is deselected, reset 'OutcomeOtherExplanation' to an empty object
    this.update({
      SentenceJudgementOtherExplanation: selected?.includes('Other') ? this.props.SentenceJudgementsOtherExplanation : {},
      SentenceJudgements: { values: selected }
    });
  };

  updateSentenceJudgementOtherExplanation = (values: LegalCriminalChargeModel['SentenceJudgementOtherExplanation']) => {
    this.update({
      SentenceJudgementOtherExplanation: values,
    });
  };

  updateProbationDates = (values: LegalCriminalChargeModel['ProbationDates']) => {
    this.update({
      ProbationDates: values,

    });
  };

  updateIncarcerationDates = (values: LegalCriminalChargeModel['IncarcerationDates']) => {
    this.update({
      IncarcerationDates: values,

    });
  };

  updateGrantedParole = (values: LegalCriminalChargeModel['GrantedParole']) => {
    this.update({
      GrantedParole: values,

    });
  };

  updateGrantedParoleDates = (values: LegalCriminalChargeModel['GrantedParoleDates']) => {
    this.update({
      GrantedParoleDates: values,

    });
  };

  render() {
    const attribute = `${this.props.attribute}.items[${this.props.$index}].Item`;
    const prefix = 'legal.pvq.criminalConvictions.hasCriminalConvictions.items';
    return (
      <div>
        <Field
          title={i18n.t(`${prefix}.courtCharge`)} // What were you charged with
          adjustFor="buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtCharge`}
          className="no-margin-bottom"
        >
          <Text
            {...this.props.CourtCharge}
            name="CourtCharge"
            className="criminalconviction-courtcharge"
            onUpdate={this.updateCourtCharge}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.CourtCharge`}
          />
        </Field>
        <Field
          title={i18n.t(`${prefix}.chargeType`)} // 'Type of charge'
          titleSize="label"
          adjustFor="buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ChargeType`}
          className="no-margin-bottom"
        >
          <Dropdown
            attribute={`${attribute}.ChargeType`}
            name="ChargeType"
            onError={this.props.onError}
            onUpdate={this.updateChargeType}
            value={this.props.ChargeType?.value}
            required={this.props.required}
          >
            {chargeTypes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>
        </Field>
        <Show when={this.props.ChargeType?.value === 'Other'}>
          <div style={{ marginBottom: '8rem' }}>
            <Field
              title={i18n.t(`${prefix}.chargeTypeOtherExplanation`)}
              titleSize="h4"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.ChargeTypeOtherExplanation`}
            >
              <Textarea
                {...this.props.ChargeTypeOtherExplanation}
                name="ChargeTypeOtherExplanation"
                className="criminalconviction-explanation"
                onUpdate={this.updateChargeTypeOtherExplanation}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.ChargeTypeOtherExplanation`}
              />
            </Field>
          </div>
        </Show>
        <Field
          title={i18n.t(`${prefix}.courtOutcome`)} // Court Outcome
          titleSize="label"
          adjustFor="buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtOutcome`}
          className="no-margin-bottom"
        >
          <Dropdown
            attribute={`${attribute}.CourtOutcome`}
            name="CourtOutcome"
            onError={this.props.onError}
            onUpdate={this.updateCourtOutcome}
            value={this.props.CourtOutcome?.value}
            required={this.props.required}
          >
            {criminalOutcomeSentenceOverOneYearOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Dropdown>
        </Field>
        <Show when={this.props.CourtOutcome?.value === 'Other'}>
          <div style={{ marginBottom: '8rem' }}>
            <Field
              title={i18n.t(`${prefix}.courtOutcomeOtherExplanation`)} // "Please explain"
              titleSize="h4"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.CourtOutcomeOtherExplanation`}
            >
              <Textarea
                {...this.props.CourtOutcomeOtherExplanation}
                name="CourtOutcomeOtherExplanation"
                className="offense-explanation"
                onUpdate={this.updateCourtOutcomeOtherExplanation}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.CourtOutcomeOtherExplanation`}
              />
            </Field>
          </div>
        </Show>

        {this.props.CourtOutcome?.value === 'Guilty' && (
          <>
            <br />
            <Field
              title={i18n.t(`${prefix}.convictedDate`)} // When were you convicted
              // help="legal.nonCriminalAction.help.civilActionDate"
              adjustFor="datecontrol"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.ConvictedDate`}
            >
              <DateControl
                {...this.props.ConvictedDate}
                attribute={`${attribute}.ConvictedDate`}
                name="ConvictedDate"
                className="criminalconviction-date"
                minDateEqualTo
                onUpdate={this.updateConvictedDate}
                onError={this.props.onError}
                required={this.props.required}
                hideDay
              />
            </Field>

            <div>
              <Field
                title={i18n.t(`${prefix}.sentenceJudgements`)} // What was the sentence
                adjustFor="p"
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.SentenceJudgements`}
              >
                <CheckboxGroup
                  className="charge-dismissed option-list option-list-vertical"
                  onError={this.props.onError}
                  required={this.props.required}
                  selectedValues={(this.props.SentenceJudgements || {}).values}
                  attribute={`${attribute}.SentenceJudgements`}
                >
                  {criminalSentenceOptions.map((s) => (
                    <Checkbox
                      name="sentence-judgement"
                      label={s}
                      value={s}
                      className="legal-charge-sentence-judgement-checkbox"
                      onUpdate={this.updateSentenceJudgements}
                      onError={this.props.onError}
                    />
                  ))}
                </CheckboxGroup>
              </Field>
              <Show when={this.props.SentenceJudgements?.values?.includes('Other')}>
                <Field
                  title={i18n.t(`${prefix}.sentenceJudgementOtherExplanation`)}
                  titleSize="h4"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.SentenceJudgementOtherExplanation`}
                >
                  <Textarea
                    {...this.props.SentenceJudgementOtherExplanation}
                    className="sentence-judgement-other-explanation"
                    name="awaiting_trial_explanation"
                    onError={this.props.onError}
                    required={this.props.required}
                    onUpdate={this.updateSentenceJudgementOtherExplanation}
                    attribute={`${attribute}.SentenceJudgementOtherExplanation`}
                  />
                </Field>
              </Show>

            </div>

            {this.props.SentenceJudgements?.values?.includes('Probation') && (
              <Field
                title={i18n.t(`${prefix}.probationDates`)} // What were the dates of your probation
                titleSize="h4"
                adjustFor="daterange"
                shrink
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.ProbationDates`}
              >
                <DateRange
                  {...this.props.ProbationDates}
                  name="ProbationDates"
                  className="probation-dates"
                  minDateEqualTo
                  onUpdate={this.updateProbationDates}
                  onError={this.props.onError}
                  required={this.props.required}
                  allowPresent
                  hideFromDay
                  hideToDay
                  attribute={`${attribute}.ProbationDates`}
                />
              </Field>
            )}

            {this.props.SentenceJudgements?.values?.includes('Imprisonment') && (
              <Field
                title={i18n.t(`${prefix}.incarceratedDates`)} // When were you imprisoned
                titleSize="h4"
                adjustFor="daterange"
                shrink
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.IncarcerationDates`}
              >

                <DateRange
                  {...this.props.IncarcerationDates}
                  name="IncarcerationDates"
                  className="incarceration-dates"
                  minDateEqualTo
                  onUpdate={this.updateIncarcerationDates}
                  onError={this.props.onError}
                  required={this.props.required}
                  allowPresent={false}
                  hideFromDay
                  hideToDay
                  attribute={`${attribute}.IncarcerationDates`}
                />

              </Field>

            )}

            <Branch
              {...this.props.GrantedParole}
              name="granted_parole"
              label={i18n.t(`${prefix}.grantedParole`)} // Were you granted parole or supervised release?
              labelSize="h4"
              className="granted_parole"
              onError={this.props.onError}
              required={this.props.required}
              onUpdate={this.updateGrantedParole}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.GrantedParole`}
              exactAttribute
            />
            {(this.props.GrantedParole?.value === 'Yes') && (
              <Field
                title={i18n.t(`${prefix}.grantedParoleDates`)}
                titleSize="h4"
                adjustFor="daterange"
                shrink
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.GrantedParoleDates`}
              >

                <DateRange
                  {...this.props.GrantedParoleDates} // What were of the dates of parole
                  name="GrantedParoleDates"
                  className="granted-parole-dates"
                  minDateEqualTo
                  present={false}
                  onUpdate={this.updateGrantedParoleDates}
                  onError={this.props.onError}
                  required={this.props.required}
                  allowPresent
                  hideFromDay
                  hideToDay
                  attribute={`${attribute}.GrantedParoleDates`}
                />

              </Field>
            )}
          </>
        )}

      </div>
    );
  }
}

CriminalCharge.defaultProps = {
  scrollIntoView: false,
  onError: (value, arr: ErrorCode) => arr,
  required: false,
  onUpdate: () => { },
  CourtCharge: {},
  ChargeType: {},
  ChargeTypeOtherExplanation: {},
  CourtOutcome: {},
  CourtOutcomeOtherExplanation: {},
  ConvictedDate: {},
  SentenceJudgements: {},
  SentenceJudgementOtherExplanation: {},
  ProbationDates: {},
  IncarcerationDates: {},
  GrantedParole: {},
  GrantedParoleDates: {},
};
